import { MinusIcon, PlusIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex, IconButton, Text } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSliderInput } from 'components/common/form/slider';
import { CommonTextInput } from 'components/common/form/text';
import { DotDotDot } from 'components/common/layout/dot-dot-dot';
import { VideoPlaceholder } from 'components/common/video/placeholder';
import useVideoLibrarySidebarStore from 'components/sections/video-library/sidebar/use-video-library-store';
import { MachineContext } from 'contexts/machine.context';
import { BASEBALL_PNG_PX } from 'enums/canvas';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { round } from 'lib_ts/classes/math.utilities';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { MS_LIMITS } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { clamp, clone } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-input-slider';
import { VideosService } from 'services/videos.service';
import { useVideosStore } from 'stores/use-videos-store';

// how thick the outline should be when dragging
const DRAGGING_OUTLINE_PX = 2;

const RELEASE_HEIGHT_FT = {
  STEP: 0.01,
  MIN: 2, // avoid MOUND_HEIGHT to avoid dividing by zero
  MAX: MS_LIMITS.POSITION.Z.MAX,
};

const RELEASE_FRAMES_PER_REQUEST = 4;

interface ISliderCoord {
  x: number;
  y: number;
}

// used for both release and mound frame
export const VideoLibrarySidebarRawFrame = (
  props: IVerboseComponent & {
    isMound?: boolean;
  }
) => {
  const model = useVideoLibrarySidebarStore((s) => s.model);

  const { machine } = useContext(MachineContext);
  const selectedVideo = useVideosStore((s) => s.selectedVideo);

  const aspectRatio = useMemo(
    () =>
      selectedVideo ? VideoHelper.getAspectRatio(selectedVideo) : undefined,
    [selectedVideo]
  );

  const [slider, setSlider] = useState<ISliderCoord>(
    props.isMound
      ? {
          x: selectedVideo?.MoundPixelX ?? 0,
          y: selectedVideo?.MoundPixelY ?? 0,
        }
      : {
          x: selectedVideo?.ReleasePixelX ?? 0,
          y: selectedVideo?.ReleasePixelY ?? 0,
        }
  );

  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);

  // update model from slider 1x after dragging finishes
  useEffect(() => {
    if (dragging) {
      return;
    }

    useVideoLibrarySidebarStore.getState().mergeModel(
      props.isMound
        ? {
            MoundPixelX: slider.x,
            MoundPixelY: slider.y,
          }
        : {
            ReleasePixelX: slider.x,
            ReleasePixelY: slider.y,
          }
    );
  }, [props.isMound, dragging]);

  // key is frame number as a string
  // value is base64 of image
  const [frameDict, setFrameDict] = useState<DictOf<string>>({});

  // used by hold interval to check the latest dict for whether next frame exists
  const latestFrameDict = useLatest(frameDict);

  // clear loading flag only after frameDict updates
  useEffect(() => {
    setLoading(false);
  }, [frameDict]);

  const [frameIndex, setFrameIndex] = useState(() => {
    if (props.isMound) {
      return 1;
    }

    return model?.ReleaseFrame ?? 1;
  });

  // automatically update model ReleaseFrame and ReleaseTime when frameIndex changes
  useEffect(() => {
    if (props.isMound) {
      // skip for mound frame
      return;
    }

    if (!selectedVideo) {
      return;
    }

    if (!model) {
      return;
    }

    if (model.ReleaseFrame === frameIndex) {
      return;
    }

    verboseDebug(props, `frameIndex changed to ${frameIndex}`);

    const timeFromFrame = VideoHelper.convertFrameToTime({
      frame: frameIndex,
      fps: selectedVideo.fps,
      maxFrame: selectedVideo.n_frames,
    });

    useVideoLibrarySidebarStore.getState().mergeModel({
      ReleaseFrame: frameIndex,
      ReleaseTime: round(timeFromFrame, 6),
    });
  }, [frameIndex]);

  // e.g. after frameDict is updated in fetchImages, auto-update image shown based on frameIndex
  const frameImage = useMemo(
    () => frameDict[frameIndex],
    [frameDict, frameIndex]
  );

  const fetchImages = async (iFrame: number, direction: -1 | 0 | 1) => {
    if (!selectedVideo) {
      verboseDebug(props, 'fetch images > skipped > no selected video');
      return;
    }

    if (!model) {
      verboseDebug(props, 'fetch images > skipped > no model');
      return;
    }

    if (loading) {
      verboseDebug(props, 'fetch images > skipped > loading');
      return;
    }

    /** how many extra frames to fetch
     * use a smaller number for smaller/faster requests
     * use a larger number for larger/fewer requests
     */
    const extraFrames = props.isMound ? 0 : RELEASE_FRAMES_PER_REQUEST;

    const indices = ArrayHelper.range(iFrame, direction * extraFrames).filter(
      (i) => i >= 1 && i <= selectedVideo.n_frames
    );

    if (indices.length === 0) {
      return;
    }

    verboseDebug(props, 'fetch images > indices', indices);

    setLoading(true);

    const results = await VideosService.getInstance().extractFrames({
      mode: 'raw',
      video_id: selectedVideo._id,
      frameSpec: model,
      frame_indices: indices,
      scales: [1],
      hardware_version: machine.hardware_version,
    });

    setFrameDict((prev) => {
      const nextDict = clone(prev);

      results.forEach((frame) => {
        nextDict[frame.frame] = frame.base64;
      });

      return nextDict;
    });

    // * loading should revert to false after frameDict is updated, to avoid racing
    // setLoading(false);
  };

  useEffect(() => {
    if (props.isMound) {
      // get just the mound frame
      fetchImages(1, 0);
      return;
    }

    // get a couple frames before and after the starting frame
    fetchImages(frameIndex - Math.floor(RELEASE_FRAMES_PER_REQUEST / 2), 1);
  }, []);

  // when the user presses and releases quickly
  // => the interval doesn't resolve before it's cleared
  const handlePressFrame = (direction: -1 | 1) => {
    verboseDebug(props, `press > direction > ${direction}`);

    if (!selectedVideo) {
      return;
    }

    const nextIndex = frameIndex + direction;

    if (nextIndex < 1 || nextIndex > selectedVideo.n_frames) {
      // shouldn't trigger
      verboseDebug(
        props,
        `press > skipped illegal nextIndex > ${nextIndex} / ${selectedVideo.n_frames}`
      );
      return;
    }

    const nextImage = frameDict[nextIndex];

    if (nextImage) {
      // use a cached value
      verboseDebug(props, `press > go to ${nextIndex}`);
      setFrameIndex(nextIndex);
      return;
    }

    // trigger a load of more images
    fetchImages(nextIndex, direction);

    // go to next frame and wait
    setFrameIndex(nextIndex);
  };

  const holdFrameInterval = useRef<NodeJS.Timeout | null>(null);

  const handleHoldFrameEnd = () => {
    if (holdFrameInterval.current) {
      verboseDebug(props, 'hold end > clearing non-null interval');
      clearInterval(holdFrameInterval.current);
      holdFrameInterval.current = null;
    }
  };

  const handleHoldFrameStart = (direction: -1 | 1) => {
    verboseDebug(props, `hold start > direction > ${direction}`);

    holdFrameInterval.current = setInterval(() => {
      setFrameIndex((prev) => {
        if (loading) {
          // already loading, don't go further until it's done
          verboseDebug(props, `hold interval > stay on ${prev} while loading`);
          return prev;
        }

        const nextIndex = prev + direction;
        const nextFrame = latestFrameDict.current[nextIndex];

        if (!nextFrame) {
          // todo: this seems kind of illegal...
          fetchImages(nextIndex, direction);
        }

        verboseDebug(props, `hold interval > go to ${nextIndex}`);
        return nextIndex;
      });
    }, 300);
  };

  // when the user presses and releases quickly
  // => the interval doesn't resolve before it's cleared
  const handlePressHeight = (direction: -1 | 1) => {
    verboseDebug(props, `press > direction > ${direction}`);

    if (!model) {
      return;
    }

    const nextHeight = clamp(
      model.ReleaseHeight + direction * RELEASE_HEIGHT_FT.STEP,
      RELEASE_HEIGHT_FT.MIN,
      RELEASE_HEIGHT_FT.MAX
    );

    useVideoLibrarySidebarStore
      .getState()
      .mergeModel({ ReleaseHeight: nextHeight });
  };

  const holdHeightInterval = useRef<NodeJS.Timeout | null>(null);

  const handleHoldHeightEnd = () => {
    if (holdHeightInterval.current) {
      verboseDebug(props, 'hold end > clearing non-null interval');
      clearInterval(holdHeightInterval.current);
      holdHeightInterval.current = null;
    }
  };

  const latestModel = useLatest(model);

  const handleHoldHeightStart = (direction: -1 | 1) => {
    verboseDebug(props, `hold start > direction > ${direction}`);

    holdHeightInterval.current = setInterval(() => {
      if (!latestModel.current) {
        return;
      }

      const nextHeight = clamp(
        latestModel.current.ReleaseHeight + direction * RELEASE_HEIGHT_FT.STEP,
        RELEASE_HEIGHT_FT.MIN,
        RELEASE_HEIGHT_FT.MAX
      );

      useVideoLibrarySidebarStore
        .getState()
        .mergeModel({ ReleaseHeight: nextHeight });
    }, 300);
  };

  const description = props.isMound
    ? `Match the indicator with the position of the pitcher's foot on the mound.`
    : `Match the ball icon with the position of the ball in the pitcher's hand.`;

  if (!selectedVideo) {
    return null;
  }

  if (!model) {
    return null;
  }

  return (
    <ErrorBoundary identifier="VideoLibrarySidebarRawFrame">
      <CommonFormGrid columns={1}>
        {!props.isMound && (
          <>
            <CommonSliderInput
              id="video-release-height"
              name="release_height"
              label="pd.release-height"
              value={model.ReleaseHeight}
              min={RELEASE_HEIGHT_FT.MIN}
              max={RELEASE_HEIGHT_FT.MAX}
              step={RELEASE_HEIGHT_FT.STEP}
              disabled={loading}
              onChange={(v) =>
                useVideoLibrarySidebarStore
                  .getState()
                  .mergeModel({ ReleaseHeight: v })
              }
            />

            {/* custom stepper for release height */}
            <Flex gap={RADIX.FLEX.GAP.SM} justify="center">
              <Box>
                <IconButton
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  disabled={
                    loading || model.ReleaseHeight <= RELEASE_HEIGHT_FT.MIN
                  }
                  onPointerDown={() => handleHoldHeightStart(-1)}
                  onPointerUp={handleHoldHeightEnd}
                  onPointerLeave={handleHoldHeightEnd}
                  onClick={() => handlePressHeight(-1)}
                >
                  <MinusIcon />
                </IconButton>
              </Box>
              <Box flexGrow="1">
                <Button
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  className="btn-block"
                >
                  {t('pd.release-height')}: {model.ReleaseHeight.toFixed(2)} ft
                </Button>
              </Box>
              <Box>
                <IconButton
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  disabled={
                    loading || model.ReleaseHeight >= RELEASE_HEIGHT_FT.MAX
                  }
                  onPointerDown={() => handleHoldHeightStart(1)}
                  onPointerUp={handleHoldHeightEnd}
                  onPointerLeave={handleHoldHeightEnd}
                  onClick={() => handlePressHeight(1)}
                >
                  <PlusIcon />
                </IconButton>
              </Box>
            </Flex>

            <CommonSliderInput
              id="video-release-frame"
              label="videos.release-frame"
              value={frameIndex}
              min={1}
              max={selectedVideo.n_frames}
              step={1}
              disabled={loading}
              onChange={(v) => {
                if (v === frameIndex) {
                  return;
                }

                if (!frameDict[v]) {
                  // in case jumping to a frame that's not been loaded
                  const direction = v < frameIndex ? -1 : 1;
                  fetchImages(v, direction);
                }

                setFrameIndex(v);
              }}
            />

            {/* custom stepper for release frame */}
            <Flex gap={RADIX.FLEX.GAP.SM} justify="center">
              <Box>
                <IconButton
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  disabled={loading || frameIndex === 1}
                  onPointerDown={() => handleHoldFrameStart(-1)}
                  onPointerUp={handleHoldFrameEnd}
                  onPointerLeave={handleHoldFrameEnd}
                  onClick={() => handlePressFrame(-1)}
                >
                  <MinusIcon />
                </IconButton>
              </Box>
              <Box flexGrow="1">
                <Button
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  className="btn-block"
                >
                  {t('videos.release-frame')}: {frameIndex}
                </Button>
              </Box>
              <Box>
                <IconButton
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  disabled={loading || frameIndex === selectedVideo.n_frames}
                  onPointerDown={() => handleHoldFrameStart(1)}
                  onPointerUp={handleHoldFrameEnd}
                  onPointerLeave={handleHoldFrameEnd}
                  onClick={() => handlePressFrame(1)}
                >
                  <PlusIcon />
                </IconButton>
              </Box>
            </Flex>
          </>
        )}

        <Box>
          <Text>{props.isMound ? 'Set Mound Frame' : 'Set Release Frame'}</Text>

          <Box mt="1">
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              {description}
            </Text>
          </Box>
        </Box>

        <div
          style={{
            borderRadius: 'var(--radius-4)',
            width: '100%',
            height: 'auto',
            aspectRatio: aspectRatio,
          }}
        >
          {loading && (
            <VideoPlaceholder aspectRatio={aspectRatio}>
              <DotDotDot label="common.loading" />
            </VideoPlaceholder>
          )}

          {!loading && !frameImage && (
            <VideoPlaceholder aspectRatio={aspectRatio}>
              {t('videos.image-extraction-error-msg')}
            </VideoPlaceholder>
          )}

          {!loading && frameImage && (
            <Slider
              axis="xy"
              xstep={1}
              xmin={0}
              xmax={selectedVideo.cap_size_1}
              x={slider.x}
              ystep={1}
              ymin={0}
              ymax={selectedVideo.cap_size_0}
              y={slider.y}
              onChange={(values) => setSlider(values)}
              onDragStart={() => setDragging(true)}
              onDragEnd={() => setDragging(false)}
              styles={{
                track: {
                  borderRadius: 'var(--radius-4)',
                  backgroundColor: 'rgba(0, 0, 255, 0)',
                  backgroundImage: `url(${frameImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  aspectRatio: aspectRatio,
                  width: '100%',
                  height: 'auto',
                },
                thumb: props.isMound
                  ? {
                      backgroundImage: 'url(/img/mound.png)',
                      backgroundColor: 'rgba(0, 255, 0, 0)',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      boxShadow: 'none',
                      width: 140,
                      height: 140,
                    }
                  : {
                      backgroundImage: `url(${
                        dragging
                          ? '/img/baseball-30-opacity.png'
                          : '/img/baseball.png'
                      })`,
                      backgroundColor: 'rgba(0, 255, 0, 0)',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      borderRadius: '50%',
                      boxShadow: 'inset 0 0 0 1px var(--gray-a6)',
                      border: `${DRAGGING_OUTLINE_PX}px solid ${
                        dragging ? 'var(--accent-10)' : 'rgba(0,0,0,0)'
                      }`,
                      height: BASEBALL_PNG_PX,
                      width: BASEBALL_PNG_PX,
                      margin: -(BASEBALL_PNG_PX + 2 * DRAGGING_OUTLINE_PX) / 2,
                    },
              }}
            />
          )}
        </div>

        <CommonFormGrid columns={2}>
          <CommonTextInput
            id="video-release-x"
            label="X Coordinate"
            type="number"
            value={slider.x.toString()}
            disabled={loading}
            onNumericChange={(v) => {
              useVideoLibrarySidebarStore
                .getState()
                .mergeModel(
                  props.isMound ? { MoundPixelX: v } : { ReleasePixelX: v }
                );

              setSlider((prev) => ({
                ...prev,
                x: v,
              }));
            }}
          />

          <CommonTextInput
            id="video-release-y"
            label="Y Coordinate"
            type="number"
            value={slider.y.toString()}
            disabled={loading}
            onNumericChange={(v) => {
              useVideoLibrarySidebarStore
                .getState()
                .mergeModel(
                  props.isMound ? { MoundPixelY: v } : { ReleasePixelY: v }
                );

              setSlider((prev) => ({
                ...prev,
                y: v,
              }));
            }}
          />
        </CommonFormGrid>
      </CommonFormGrid>
    </ErrorBoundary>
  );
};
